import type { ApiClient } from '@classgo/app-kit';
import type {
  BillingCalendarInput,
  BillingCalendarView,
  CalendarRunResult,
  ChargeView,
  CreateFeeConceptInput,
  CreatePaymentInput,
  FamilyAccountView,
  FamilyStatement,
  FeeConceptView,
  GatewayIntentView,
  GatewaySettingsView,
  GenerateChargesInput,
  GenerateChargesResult,
  PageOf,
  PaymentView,
  PutTariffsInput,
  ReminderSettingsInput,
  ReminderSettingsView,
  RunCalendarsInput,
} from '@classgo/shared';
import { queryOptions } from '@tanstack/react-query';

const qs = (query: Record<string, string | number | boolean | undefined>) => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const text = params.toString();
  return text ? `?${text}` : '';
};

/**
 * The billing endpoints (tickets #59, #64, #65, #109): concepts and tariffs, charge generation,
 * families and statements, payments, the payment link, the invoicing export, the calendar of a
 * recurring concept with the run of one month, and the school's reminder days.
 *
 * @param {ApiClient} api -- The client.
 * @returns The calls.
 */
export const billingApi = (api: ApiClient) => ({
  createConcept: (body: CreateFeeConceptInput) =>
    api.request<FeeConceptView>('/billing/concepts', { method: 'POST', body }),
  putTariffs: (conceptId: string, body: PutTariffsInput) =>
    api.request<FeeConceptView>(`/billing/concepts/${conceptId}/tariffs`, { method: 'PUT', body }),
  generate: (body: GenerateChargesInput) =>
    api.request<GenerateChargesResult>('/billing/charges/generate', { method: 'POST', body }),
  recordPayment: (body: CreatePaymentInput) =>
    api.request<PaymentView>('/billing/payments', { method: 'POST', body }),
  checkout: (chargeId: string) =>
    api.request<GatewayIntentView>(`/billing/charges/${chargeId}/checkout`, { method: 'POST' }),
  exportFile: (kind: 'charges' | 'payments', from: string, to: string, format: 'csv' | 'xlsx') =>
    api.download(`/billing/exports${qs({ kind, from, to, format })}`),
  putCalendar: (conceptId: string, body: BillingCalendarInput) =>
    api.request<BillingCalendarView>(`/billing/concepts/${conceptId}/calendar`, {
      method: 'PUT',
      body,
    }),
  runCalendars: (body: RunCalendarsInput) =>
    api.request<CalendarRunResult>('/billing/calendars/run', { method: 'POST', body }),
  putReminderSettings: (body: ReminderSettingsInput) =>
    api.request<ReminderSettingsView>('/billing/reminder-settings', { method: 'PUT', body }),
});

/**
 * Query options of the billing screens.
 */
export const billingQueries = {
  concepts: (api: ApiClient, schoolYearId: string) =>
    queryOptions({
      queryKey: ['billing', 'concepts', schoolYearId],
      queryFn: () => api.request<FeeConceptView[]>(`/billing/concepts${qs({ schoolYearId })}`),
      enabled: schoolYearId !== '',
    }),
  families: (api: ApiClient, query: { search?: string; withBalance?: boolean; page?: number }) =>
    queryOptions({
      queryKey: ['billing', 'families', query],
      queryFn: () =>
        api.request<PageOf<FamilyAccountView>>(
          `/billing/families${qs({ pageSize: 50, ...query })}`,
        ),
    }),
  statement: (api: ApiClient, guardianId: string) =>
    queryOptions({
      queryKey: ['billing', 'statement', guardianId],
      queryFn: () => api.request<FamilyStatement>(`/billing/families/${guardianId}/statement`),
      enabled: guardianId !== '',
    }),
  charges: (api: ApiClient, query: { guardianId?: string; status?: string }) =>
    queryOptions({
      queryKey: ['billing', 'charges', query],
      queryFn: () =>
        api.request<PageOf<ChargeView>>(`/billing/charges${qs({ pageSize: 200, ...query })}`),
    }),
  gateway: (api: ApiClient) =>
    queryOptions({
      queryKey: ['billing', 'gateway'],
      queryFn: () => api.request<GatewaySettingsView>('/billing/gateway'),
      retry: false,
    }),
  calendars: (api: ApiClient, schoolYearId: string) =>
    queryOptions({
      queryKey: ['billing', 'calendars', schoolYearId],
      queryFn: () =>
        api.request<{ items: BillingCalendarView[] }>(`/billing/calendars${qs({ schoolYearId })}`),
      enabled: schoolYearId !== '',
    }),
  reminderSettings: (api: ApiClient) =>
    queryOptions({
      queryKey: ['billing', 'reminder-settings'],
      queryFn: () => api.request<ReminderSettingsView>('/billing/reminder-settings'),
    }),
};
